// client/src/utils/validation.ts
export const isValidIsraeliID = (id: string): boolean => {
  if (!/^\d{8,9}$/.test(id)) return false; // must be 8 or 9 digits
  const s = id.padStart(9, "0").split("").reduce((sum, d, i) => {
    const x = (+d) * ((i % 2) + 1);
    return sum + (x > 9 ? x - 9 : x);
  }, 0);
  return s % 10 === 0;
};

const isName = (v: string) => /^[A-Za-zא-ת .'-]+$/.test(v.trim());
const isDigits = (v: string) => /^\d+$/.test(v.trim());
const isEmail = (v: string) => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v.trim());

export type Form = {
  id: string; fullname: string; email: string; phone: string; details: string; agree: boolean; liability: boolean; privacy: boolean; read: boolean;
};
export type Errors = Partial<Record<keyof Form, string>> & { files?: string };

export const fieldError = (name: string, value: string): string | undefined => {
  switch (name) {
    case "id":
      if (!value.trim()) return "נא להזין מספר תעודת זהות בעל 9 ספרות";
      if (!/^\d*$/.test(value)) return "יש להקליד מספר תעודת זהות בעל 9 ספרות";
      if (value && !isValidIsraeliID(value)) return "מספר תעודת זהות לא תקין";
      return;
    case "firstname":
      if (!value.trim()) return "נא להזין שם פרטי";
      if (!isName(value)) return "שם פרטי יכול להכיל אותיות בלבד";
      return;
    case "lastname":
      if (!value.trim()) return "נא להזין שם משפחה";
      if (!isName(value)) return "שם משפחה יכול להכיל אותיות בלבד";
      return;
    case "fullname":
    case "fullName":
      if (!value) return "יש להזין שם מלא";
      if (typeof value === 'string' && value.trim().split(/\s+/).length < 2) {
        return "יש להזין שם פרטי ושם משפחה";
      }
      return;
    case "orderNumber":
      if (!value.trim()) return "נא להזין מספר הזמנה";
      return;
    case "phone":
      if (!value.trim()) return "נא להזין מספר טלפון";
      if (!isDigits(value)) return "מספר טלפון חייב להכיל ספרות בלבד";
      if (value.length < 9 || value.length > 13) return "מספר טלפון חייב להכיל בין 9 ל-13 ספרות";
      return;
    case "email":
      if (!value.trim()) return "נא להזין כתובת אימייל";
      if (!isEmail(value)) return "כתובת אימייל לא תקינה";
      return;
    case "reason":
      if (!value.trim()) return "נא להזין סיבת ביטול";
      return;
    case "details":
      if (!value.trim()) return "נא להזין פרטים נוספים";
      return;
    case "itemsDescription":
      if (!value.trim()) return "נא להזין תיאור פריט";
      return;
    case "city":
      if (!value.trim()) return "נא להזין עיר";
      return;
    case "street":
      if (!value.trim() || value.trim().split(/\s+/).length < 2) return "נא להזין רחוב ומספר בית";
      return;
    case "subject":
      if (!value) return "יש לבחור נושא";
      return;
    case "subSubject":
      if (!value) return "יש לבחור נושא משני";
      return;
    case "message":
      if (!value.trim()) return "נא להזין תוכן פנייה";
      return;
    default:
      return;
  }
};

const ONLINE_SUBJECT_ID = 'online';

export interface ContactUsFormValues {
  subject: string;
  subSubject: string;
  orderNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

export function isOrderNumberRequired(
  subjectId: string,
  subSubjectId: string,
  subSubjects: { value: string; requiresOrderNumber?: boolean }[]
): boolean {
  if (subjectId !== ONLINE_SUBJECT_ID || !subSubjectId) return false;
  const match = subSubjects.find((s) => s.value === subSubjectId);
  return match?.requiresOrderNumber === true;
}

export function contactUsFieldErrors(
  form: ContactUsFormValues,
  onlineSubSubjects: { value: string; requiresOrderNumber?: boolean }[] = []
): Partial<Record<keyof ContactUsFormValues, string>> {
  const errors: Partial<Record<keyof ContactUsFormValues, string>> = {
    subject: fieldError('subject', form.subject),
    firstName: fieldError('firstname', form.firstName),
    lastName: fieldError('lastname', form.lastName),
    email: fieldError('email', form.email),
    phone: fieldError('phone', form.phone),
    message: fieldError('message', form.message),
  };

  if (form.subject === ONLINE_SUBJECT_ID) {
    errors.subSubject = fieldError('subSubject', form.subSubject);
    if (isOrderNumberRequired(form.subject, form.subSubject, onlineSubSubjects)) {
      errors.orderNumber = fieldError('orderNumber', form.orderNumber);
    } else {
      errors.orderNumber = undefined;
    }
  } else {
    errors.subSubject = undefined;
    errors.orderNumber = undefined;
  }

  return errors;
}
